import type { IContainer } from '@monofe/di';
import { injectOrBuildContainer } from '@monofe/di';
import TYPES from '../util/types';

export type ProgressNotifier = (progress: number) => void;

export interface IProgressPromise<ValueType> extends Promise<ValueType> {
    readonly progress: number;
    onProgress: (callback: ProgressNotifier) => void;
}

export type PromiseExecutor<ValueType> = (
    resolve: (value?: PromiseLike<ValueType> | ValueType) => void,
    reject: (reason?: unknown) => void,
    progress: ProgressNotifier,
) => PromiseLike<ValueType> | ValueType | undefined;

export type PromiseAsyncExecutor<ValueType> = (
    progress: ProgressNotifier,
) => PromiseLike<ValueType> | ValueType | undefined;

export interface IProgressPromiseGenerator {
    async: <ValueType>(executor: PromiseAsyncExecutor<ValueType>) => IProgressPromise<ValueType>;
    promise: <ValueType>(executor: PromiseExecutor<ValueType>) => IProgressPromise<ValueType>;
    retry: <ValueType>(
        factory: () => IProgressPromise<ValueType>,
        delayBetweenRetries?: number,
        retryCount?: number,
    ) => IProgressPromise<ValueType>;
}

export function injectProgressPromise(
    container: IContainer = injectOrBuildContainer(),
): IProgressPromiseGenerator {
    return container.get<IProgressPromiseGenerator>(TYPES.IProgressPromiseGenerator);
}

export function bindProgressPromise(
    val: IProgressPromiseGenerator,
    container: IContainer = injectOrBuildContainer(),
): void {
    container.bind<IProgressPromiseGenerator>(TYPES.IProgressPromiseGenerator, val);
}

export function unbindProgressPromise(container: IContainer = injectOrBuildContainer()): void {
    container.remove(TYPES.IProgressPromiseGenerator);
}
